import React from 'react';
import { CustomCellRendererProps } from 'ag-grid-react';
import styled, { css } from '@benzinga/themetron';
import { UnpackedArray } from '@benzinga/utils';
import { absColDef, AdvancedColDef } from '../helpers/advancedHeader';

const MARKET_PERIODS = [
  'Q1',
  'Q2',
  'Q3',
  'Q4',
  'FY',
  '1H',
  '2H',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const PeriodRender: React.FC<CustomCellRendererProps> = ({ value }) => {
  let formattedValue = value;
  const valueArr: string[] = value?.split(' ') ?? [''];
  const period = MARKET_PERIODS.find(mp => valueArr[0].toLocaleLowerCase().startsWith(mp.toLocaleLowerCase()));
  if (period) {
    valueArr[0] = period;
    formattedValue = valueArr.join(' ');
  }
  return <StyledSpan $period={period}>{formattedValue}</StyledSpan>;
};

const StyledSpan = styled.span<{ $period: UnpackedArray<typeof MARKET_PERIODS> | undefined }>`
  ${props => {
    const basePeriodCss = css`
      position: absolute;
      width: 25%;
      text-align: center;
      color: ${props.theme.colors.foregroundActive} !important;
    `;
    const baseMonthCss = css`
      position: absolute;
      width: 40px;
      text-align: center;
      color: ${props.theme.colors.foregroundActive} !important;
    `;
    const monthColor = (index: number) => css`
      background-color: hsla(${21.2 * index}, 100%, 50%, 0.3);
    `;
    switch (props.$period) {
      case 'Q1':
        return css`
          ${basePeriodCss};
          background-color: ${props.theme.colorPalette.bzRed}80;
          left: 0;
        `;
      case 'Q2':
        return css`
          ${basePeriodCss};
          background-color: ${props.theme.colorPalette.bzYellow}80;
          left: 25%;
        `;
      case 'Q3':
        return css`
          ${basePeriodCss};
          background-color: ${props.theme.colorPalette.bzGreen}80;
          right: 25%;
        `;
      case 'Q4':
        return css`
          ${basePeriodCss};
          background-color: ${props.theme.colorPalette.bzBlue}80;
          right: 0;
        `;
      case '1H':
        return css`
          ${basePeriodCss};
          width: 50%;
          left: 0;
          background: linear-gradient(
            to right,
            ${props.theme.colorPalette.bzRed}80,
            ${props.theme.colorPalette.bzYellow}80
          );
        `;
      case '2H':
        return css`
          ${basePeriodCss};
          width: 50%;
          right: 0;
          background: linear-gradient(
            to right,
            ${props.theme.colorPalette.bzGreen}80,
            ${props.theme.colorPalette.bzBlack}80
          );
        `;
      case 'FY':
        return css`
          ${basePeriodCss};
          left: 0;
          width: 100%;
          background: linear-gradient(
            to right,
            ${props.theme.colorPalette.bzRed}80 0,
            ${props.theme.colorPalette.bzYellow}80 33%,
            ${props.theme.colorPalette.bzGreen}80 66%,
            ${props.theme.colorPalette.bzBlack}80 100%
          );
        `;
      // 9.1% - 3.6px = (100%(width of cell) - 40px(width of text span)) * 1(month index) / 11(number of months)
      // creating a mixin function with calc() doesn't work
      case 'Jan':
        return css`
          ${baseMonthCss};
          left: 0;
          ${monthColor(0)};
        `;
      case 'Feb':
        return css`
          ${baseMonthCss};
          left: calc(9.1% - 3.6px);
          ${monthColor(1)};
        `;
      case 'Mar':
        return css`
          ${baseMonthCss};
          left: calc(18.2% - 7.3px);
          ${monthColor(2)};
        `;
      case 'Apr':
        return css`
          ${baseMonthCss};
          left: calc(27.3% - 10.9px);
          ${monthColor(3)};
        `;
      case 'May':
        return css`
          ${baseMonthCss};
          left: calc(36.4% - 14.5px);
          ${monthColor(4)};
        `;
      case 'Jun':
        return css`
          ${baseMonthCss};
          left: calc(45.5% - 18.2px);
          ${monthColor(5)};
        `;
      case 'Jul':
        return css`
          ${baseMonthCss};
          left: calc(54.6% - 21.2px);
          ${monthColor(6)};
        `;
      case 'Aug':
        return css`
          ${baseMonthCss};
          left: calc(63.7% - 25.5px);
          ${monthColor(7)};
        `;
      case 'Sep':
        return css`
          ${baseMonthCss};
          left: calc(72.8% - 29.1px);
          ${monthColor(8)};
        `;
      case 'Oct':
        return css`
          ${baseMonthCss};
          left: calc(81.9% - 32.7px);
          ${monthColor(9)};
        `;
      case 'Nov':
        return css`
          ${baseMonthCss};
          left: calc(91% - 36.4px);
          ${monthColor(10)};
        `;
      case 'Dec':
        return css`
          ${baseMonthCss};
          right: 0;
          ${monthColor(11)};
        `;
      default:
        return css``;
    }
  }}
`;

export const PeriodColDef = (colDef: AdvancedColDef): AdvancedColDef => ({
  cellRenderer: PeriodRender,
  filter: 'agTextColumnFilter',
  ...colDef,
  ...absColDef(colDef),
});
